import React, { useEffect, useRef } from "react"
import { Router, Route, Switch, useLocation } from "wouter"
import Lenis from "lenis"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { Navbar } from "./components/Navbar"
import { Footer } from "./components/Footer"
import { ScrollPrompt } from "./components/ScrollPrompt"
import { Home } from "./pages/Home"
import { About } from "./pages/About"
import { Services } from "./pages/Services"
import { LetsWorkTogether } from "./pages/LetsWorkTogether"
import { Workshops } from "./pages/Workshops"
import { Enablers } from "./pages/Enablers"
import { ClientPage } from "./pages/clients/ClientPage"

gsap.registerPlugin(ScrollTrigger)

const hexToRgb = hex => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [0, 0, 0]
}

const mixColor = (c1, c2, t) => {
  const [r1, g1, b1] = hexToRgb(c1)
  const [r2, g2, b2] = hexToRgb(c2)
  const lerp = (a, b) => Math.round(a + (b - a) * t)
  return `rgb(${lerp(r1, r2)},${lerp(g1, g2)},${lerp(b1, b2)})`
}

function ScrollToTop() {
  const lenisRef = React.useContext(LenisContext)
  const [location] = useLocation()
  React.useLayoutEffect(() => {
    const lenis = lenisRef.current
    if (lenis) lenis.scrollTo(0, { immediate: true })
    else window.scrollTo(0, 0)
    if (location !== "/") {
      document.body.style.backgroundColor = ""
      document.body.style.color = ""
      document.querySelectorAll(".char-default, .scroll-prompt, .hero-word, .interlude-word, .home-cta")
        .forEach(el => (el.style.color = ""))
    }
  }, [location, lenisRef])
  return null
}

export const LenisContext = React.createContext({ current: null })

export const App = () => {
  const lenisRef = useRef(null)

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true })
    lenisRef.current = lenis

    const lenisRaf = (time) => {
      lenis.raf(time * 1000)

      // Run every frame — reads real viewport position, no scroll event needed
      const s2 = document.querySelector(".home-section-2")
      if (!s2) return

      const rect = s2.getBoundingClientRect()
      const vh = window.innerHeight

      // Progress: 0 when section 3 enters viewport bottom, 1 when its top reaches viewport top
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / vh))

      const bg = mixColor("#000000", "#f0ede8", progress)
      document.body.style.backgroundColor = bg
      const textColor = mixColor("#ffffff", "#0a0a0a", progress)
      document.querySelectorAll(".hero-word, .interlude-word, .char-default, .scroll-prompt, .home-cta").forEach(el => (el.style.color = textColor))
    }

    gsap.ticker.add(lenisRaf)
    gsap.ticker.lagSmoothing(0)

    return () => {
      lenis.destroy()
      lenisRef.current = null
      gsap.ticker.remove(lenisRaf)
    }
  }, [])

  return (
    <LenisContext.Provider value={lenisRef}>
    <Router>
      <ScrollToTop />
      <Navbar />
      <ScrollPrompt />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/services" component={Services} />
        <Route path="/lets-work-together" component={LetsWorkTogether} />
        <Route path="/workshops" component={Workshops} />
        <Route path="/enablers" component={Enablers} />
        <Route path="/clients/:clientId" component={ClientPage} />
      </Switch>
      <Footer />
    </Router>
    </LenisContext.Provider>
  )
}
